import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  FormControl,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CustomTable, { IColumn } from "../../common/CustomTable/CustomTable";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
  getLookups,
  addLookUp,
  deleteLookup,
  getLookupListByName,
} from "./lookupAction";
import { ILookup } from "../../models/admin/lookup/Lookup";
import { ArrowBackOutlined, DeleteOutlineOutlined } from "@mui/icons-material";
import { EditOutlined } from "@mui/icons-material";
import { lookupFormFields } from "../../constants/Constants";
import AlertDialog from "../../components/AlertDialog/AlertDialog";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import { useNavigate, useParams } from "react-router-dom";
import { Add as AddIcon } from "@mui/icons-material";
import LookupForm from "./LookupForm";

const Lookup = () => {
  const { name } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { lookupListByName } = useAppSelector(
    (store: RootState) => store.lookup
  );
  const { isError, isSuccess, calledReducerType, isLoading } = useAppSelector(
    (store: RootState) => store.common
  );

  const defaultLookupFormData: ILookup = useMemo(
    () => ({
      name: name as string,
      code: "",
      label: "",
    }),
    [name]
  );

  const defaultErrors = useMemo(
    () => ({
      name: "",
      code: "",
      label: "",
    }),
    []
  );

  const [lookupFormData, setLookupFormData] = useState<ILookup>(
    defaultLookupFormData
  );
  const [errors, setErrors] = useState<{
    code: string;
    name: string;
    label: string;
  }>(defaultErrors);
  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");

  const openAddPopup = () => {
    setLookupFormData({ code: "", name: name as string, label: "" });
    setDialogTitle(`Add Lookup value for ${name}`);
    setIsFormOpen(true);
  };

  const openEditPopup = () => {
    setDialogTitle("Update Lookup Value");
    setIsFormOpen(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value && value.trim().length > 0) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setLookupFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateFields = () => {
    const errors = { ...defaultErrors };
    for (let key in defaultErrors) {
      if (!lookupFormData[key as keyof typeof defaultErrors].trim()) {
        errors[key as keyof typeof defaultErrors] = "Invalid input";
      }
    }
    const isNotValid = Object.keys(errors).some((item) => {
      return errors[item as keyof typeof errors].length > 0;
    });

    if (!isNotValid) {
      setErrors({ ...defaultErrors });
      return true;
    }
    setErrors({ ...errors });
    return false;
  };

  const handleSubmit = () => {
    if (validateFields()) {
      dispatch(addLookUp(lookupFormData));
      setIsFormOpen(false);
    }
  };

  const handleReset = useCallback(() => {
    setLookupFormData((prev) => ({ ...prev, code: "", label: "" }));
    setErrors({ ...defaultErrors });
  }, [defaultErrors]);

  const updateFormFields = (lookup: ILookup) => {
    setLookupFormData({ ...lookup });
  };

  const handleDeleteIconClick = (lookup: ILookup) => {
    setLookupFormData(lookup);
    setAlertDialogOpen(true);
  };

  const handleAlertDialogCancelClick = useCallback(() => {
    setAlertDialogOpen(false);
    setLookupFormData({ ...defaultLookupFormData });
  }, [defaultLookupFormData]);

  const handleAlertDialogOkClick = useCallback(() => {
    setAlertDialogOpen(false);
    dispatch(deleteLookup(lookupFormData));
  }, [dispatch, lookupFormData]);

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    dispatch(getLookups());
    if (name) {
      dispatch(getLookupListByName(name));
    }
  }, [dispatch, name]);

  const getFormFields = () => {
    return lookupFormFields.map((formField, index) => {
      return (
        index > 0 && (
          <FormControl
            key={`lookup-form-${index}`}
            sx={{ width: `50%` }}
            id={`lookup_form_field_${formField.name}`}
            data-test-id={`lookup_form_field_${formField.name}`}
            aria-label={`Lookup form field ${formField.label}`}
          >
            <TextField
              type="text"
              // label={formField.label}
              variant="outlined"
              name={formField.name}
              value={
                lookupFormData[formField.name as keyof typeof lookupFormData]
              }
              placeholder={`Please enter the lookup ${formField.label}`}
              error={
                errors[formField.name as keyof typeof lookupFormData]
                  ? true
                  : false
              }
              onChange={handleChange}
              id={`lookup_form_input_${formField.name}`}
              data-test-id={`lookup_form_input_${formField.name}`}
              aria-label={`Enter lookup ${formField.label}`}
            />
            <Typography
              sx={{ color: "red" }}
              id={`lookup_form_error_${formField.name}`}
              data-test-id={`lookup_form_error_${formField.name}`}
              aria-label={`Error message for ${formField.label}`}
            >
              {errors[formField.name as keyof typeof lookupFormData]}
            </Typography>
          </FormControl>
        )
      );
    });
  };

  const getFormSection = () => {
    return (
      <>
        <Box
          sx={{
            display: "inline-flex",
            width: "100%",
            gap: "1rem",
            paddingBottom: "8px",
          }}
          id="lookup_form_fields_container"
          data-test-id="lookup_form_fields_container"
          aria-label="Lookup form fields container"
        >
          {getFormFields()}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            gap: "20px",
          }}
          id="lookup_form_actions"
          data-test-id="lookup_form_actions"
          aria-label="Lookup form actions"
        >
          <Button
            name="submit"
            variant="contained"
            onClick={handleSubmit}
            sx={{ borderRadius: "4px" }}
            id="lookup_form_submit_button"
            data-test-id="lookup_form_submit_button"
            aria-label="Submit lookup form"
          >
            Submit
          </Button>
          <Button
            name="reset"
            variant="contained"
            onClick={handleReset}
            sx={{ borderRadius: "4px" }}
            id="lookup_form_reset_button"
            data-test-id="lookup_form_reset_button"
            aria-label="Reset lookup form"
          >
            Reset
          </Button>
        </Box>
      </>
    );
  };

  const getListSection = () => {
    const columns: IColumn[] = [
      {
        label: "Label",
        accessorKey: "label",
      },
      {
        label: "Code",
        accessorKey: "code",
      },

      {
        label: "Actions",
        accessorKey: "",
        Cell: (lookup: any) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <IconButton
              size="small"
              onClick={() => {
                updateFormFields(lookup);
                openEditPopup();
              }}
            >
              <EditOutlined
                sx={{ cursor: "pointer" }}
                titleAccess="Edit this record"
              />
            </IconButton>

            <IconButton
              size="small"
              onClick={() => handleDeleteIconClick(lookup)}
            >
              <DeleteOutlineOutlined
                sx={{ cursor: "pointer" }}
                titleAccess="Delete this record"
              />
            </IconButton>
          </Box>
        ),
      },
    ];

    return (
      <CustomTable
        columns={columns}
        data={[...lookupListByName].reverse()}
        stickyHeader={true}
        pagination={true}
        displaySkeleton={
          isLoading && calledReducerType === "lookup/getLookupListByName"
        }
      />
    );
  };

  useEffect(() => {
    if (name) {
      dispatch(getLookupListByName(name));
    }
  }, [dispatch, name]);

  useEffect(() => {
    if (
      ((isSuccess || isError) && calledReducerType === "lookup/addLookUp") ||
      ((isSuccess || isError) && calledReducerType === "lookup/deleteLookup")
    ) {
      setMessageDialogOpen(true);
      handleReset();
    }
  }, [isSuccess, isError, calledReducerType, handleReset]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
      id="lookup_section"
      data-test-id="lookup_section"
      aria-label="Lookup section"
    >
      <Box>
        <CardHeader
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openAddPopup()}
              sx={{ textTransform: "none", borderRadius: "4px" }}
              id="lookup_add_button"
              data-test-id="lookup_add_button"
              aria-label="Add new lookup"
            >
              Add Lookup
            </Button>
          }
        />
        <CardContent
          sx={{ display: "grid", gap: "20px" }}
          id="lookup_content"
          data-test-id="lookup_content"
          aria-label="Lookup content"
        >
          {getListSection()}
        </CardContent>
      </Box>
      <LookupForm
        open={isFormOpen}
        dialogTitle={dialogTitle}
        onClose={() => setIsFormOpen(false)}
        getFormSection={getFormSection}
      />
      <AlertDialog
        open={alertDialogOpen}
        handleCancelClick={handleAlertDialogCancelClick}
        handleOkClick={handleAlertDialogOkClick}
        title="Are you sure to delete this lookup?"
      />
      <MessageDialog
        open={messageDialogOpen}
        handleClose={handleMessageDialogClose}
      />
    </Box>
  );
};

export default Lookup;
